import { Helmet } from "@dr.pogodin/react-helmet";
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { STATE_TAX_SUMMARY_2024 } from "../data/stateTaxSummary";
import RecentPosts from "../components/RecentPosts";
import ExpenseTrackerBanner from "../components/ExpenseTrackerBanner";

export default function StateTaxComparison() {
  const [income, setIncome] = useState("100000");
  const [sortBy, setSortBy] = useState<"name" | "rate" | "tax">("tax");
  
  const allStates = Object.values(STATE_TAX_SUMMARY_2024);

  // Calculate effective tax for each state
  const stateData = useMemo(() => {
    const incomeNum = parseFloat(income) || 0;
    
    return allStates.map(state => {
      let estimatedTax = 0;
      let effectiveRate = 0;

      if (state.type === "none") {
        estimatedTax = 0;
        effectiveRate = 0;
      } else if (state.type === "flat") {
        effectiveRate = state.flatRate || 0;
        estimatedTax = incomeNum * effectiveRate;
      } else if (state.type === "progressive") {
        if (state.brackets) {
          // Use detailed brackets if available
          let remaining = incomeNum;
          let prevMax = 0;

          for (const bracket of state.brackets) {
            if (bracket.upTo === null) {
              // Top bracket
              estimatedTax += remaining * bracket.rate;
              break;
            } else {
              const bracketSize = bracket.upTo - prevMax;
              if (remaining > bracketSize) {
                estimatedTax += bracketSize * bracket.rate;
                remaining -= bracketSize;
                prevMax = bracket.upTo;
              } else {
                estimatedTax += remaining * bracket.rate;
                break;
              }
            }
          }
          effectiveRate = incomeNum > 0 ? estimatedTax / incomeNum : 0;
        } else if (state.range) {
          // Estimate using average of min and max rates when brackets aren't available
          const avgRate = (state.range.min + state.range.max) / 2;
          estimatedTax = incomeNum * avgRate;
          effectiveRate = avgRate;
        }
      }

      return {
        ...state,
        estimatedTax,
        effectiveRate,
        savings: 0 // Will calculate after
      };
    });
  }, [income, allStates]);

  // Calculate savings vs highest tax state
  const sortedData = useMemo(() => {
    const maxTax = Math.max(...stateData.map(s => s.estimatedTax));
    const dataWithSavings = stateData.map(s => ({
      ...s,
      savings: maxTax - s.estimatedTax
    }));

    if (sortBy === "name") {
      return [...dataWithSavings].sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === "rate") {
      return [...dataWithSavings].sort((a, b) => a.effectiveRate - b.effectiveRate);
    } else {
      return [...dataWithSavings].sort((a, b) => a.estimatedTax - b.estimatedTax);
    }
  }, [stateData, sortBy]);

  const noTaxStates = sortedData.filter(s => s.type === "none");
  const lowestTaxStates = sortedData.filter(s => s.type !== "none").slice(0, 5);
  const highestTaxStates = sortedData.filter(s => s.type !== "none").slice(-5).reverse();

  const getTaxTypeColor = (type: string) => {
    if (type === "none") return "bg-green-100 text-green-800";
    if (type === "flat") return "bg-blue-100 text-blue-800";
    return "bg-orange-100 text-orange-800";
  };

  return (
    <>
      <Helmet>
        <title>State Income Tax Comparison Tool | Best States for 1099 Contractors</title>
        <meta
          name="description"
          content="Compare income tax rates across all 50 US states for freelancers and 1099 contractors. Find the best states for self-employed tax savings."
        />
        <meta name="keywords" content="state tax comparison, best states for freelancers, no income tax states, state tax rates, self employed taxes by state" />
        <link rel="canonical" href="https://free1099calc.com/tools/state-tax-comparison" />
      </Helmet>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-emerald-900 via-emerald-800 to-slate-900 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1524661135-423995f22d0b?w=1920&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/85 to-slate-900/90" />
        
        <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-28">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block px-4 py-2 bg-emerald-500/20 backdrop-blur-sm border border-emerald-400/30 rounded-full mb-6">
              <span className="text-emerald-300 text-sm font-semibold">🗺️ State Taxes</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              State Tax Comparison
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 mb-8">
              Compare income tax rates across all 50 states and find the best location for your freelance business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#calculator"
                className="inline-block bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors shadow-lg"
              >
                Compare States →
              </a>
              <Link
                to="/tools"
                className="inline-block bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border border-white/30 px-8 py-4 rounded-lg font-semibold transition-colors"
              >
                More Tools
              </Link>
            </div>
          </div>
        </div>
      </div>

    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-12" id="calculator">

        {/* Expense Tracker Banner */}
        <div className="mb-8">
          <ExpenseTrackerBanner compact />
        </div>

        {/* Input section */}
        <div className="bg-white rounded-lg border shadow-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Your Estimated Annual Income
              </label>
              <div className="relative">
                <span className="absolute left-3 top-3 text-slate-500">$</span>
                <input
                  type="text"
                  value={income}
                  onChange={(e) => setIncome(e.target.value.replace(/[^0-9]/g, ''))}
                  className="w-full pl-8 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg"
                  placeholder="100000"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Enter your taxable income after deductions
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="tax">Total Tax (Lowest to Highest)</option>
                <option value="rate">Effective Rate</option>
                <option value="name">State Name (A-Z)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
            <h3 className="text-lg font-bold text-green-900 mb-3">No Income Tax States</h3>
            <div className="text-3xl font-bold text-green-700 mb-2">{noTaxStates.length}</div>
            <p className="text-sm text-green-800 mb-3">States with zero income tax</p>
            <div className="text-xs space-y-1">
              {noTaxStates.map(s => (
                <div key={s.code} className="text-green-700 font-medium">{s.name}</div>
              ))}
            </div>
          </div>

          <div className="bg-primary-50 border-2 border-primary-200 rounded-lg p-6">
            <h3 className="text-lg font-bold text-primary-900 mb-3">Lowest Tax States</h3>
            <p className="text-sm text-primary-800 mb-3">Besides no-tax states</p>
            <div className="text-xs space-y-2">
              {lowestTaxStates.map((s, idx) => (
                <div key={s.code} className="flex justify-between items-center">
                  <span className="font-medium text-primary-700">{idx + 1}. {s.name}</span>
                  <span className="text-primary-600">{(s.effectiveRate * 100).toFixed(2)}%</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
            <h3 className="text-lg font-bold text-red-900 mb-3">Highest Tax States</h3>
            <p className="text-sm text-red-800 mb-3">At ${parseInt(income).toLocaleString()} income</p>
            <div className="text-xs space-y-2">
              {highestTaxStates.map((s, idx) => (
                <div key={s.code} className="flex justify-between items-center">
                  <span className="font-medium text-red-700">{idx + 1}. {s.name}</span>
                  <span className="text-red-600">{(s.effectiveRate * 100).toFixed(2)}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Full comparison table */}
        <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
          <div className="p-6 border-b">
            <h2 className="text-2xl font-bold text-slate-900">All 50 States Comparison</h2>
            <p className="text-sm text-slate-600 mt-1">
              Based on ${parseInt(income).toLocaleString()} taxable income
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    State
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Tax Type
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Effective Rate
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Estimated Tax
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Savings vs Highest
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {sortedData.map((state, idx) => (
                  <tr key={state.code} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-slate-900">{state.name}</div>
                      {state.footnote && (
                        <div className="text-xs text-slate-500 mt-1">{state.footnote}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${getTaxTypeColor(state.type)}`}>
                        {state.type === "none" ? "No Tax" : state.type === "flat" ? "Flat" : "Progressive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-medium text-slate-900">
                      {(state.effectiveRate * 100).toFixed(2)}%
                    </td>
                    <td className="px-6 py-4 text-right font-bold text-slate-900">
                      ${state.estimatedTax.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-6 py-4 text-right font-medium text-green-600">
                      ${state.savings.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Educational content */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg border shadow-sm p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Choosing a State: Beyond Income Tax</h2>
            <p className="text-sm text-slate-700 mb-4">
              While income tax is important, consider these other factors when relocating:
            </p>
            <ul className="space-y-3 text-sm text-slate-700">
              <li className="flex items-start">
                <span className="text-primary-600 mr-2 mt-0.5">•</span>
                <div>
                  <strong>Sales tax:</strong> States with no income tax often have higher sales taxes (e.g., WA at 6.5%, TN at 7%)
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-600 mr-2 mt-0.5">•</span>
                <div>
                  <strong>Property tax:</strong> TX and NH have high property taxes to compensate for no income tax
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-600 mr-2 mt-0.5">•</span>
                <div>
                  <strong>Cost of living:</strong> Lower taxes may be offset by higher housing, utilities, or healthcare costs
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-600 mr-2 mt-0.5">•</span>
                <div>
                  <strong>Business climate:</strong> LLC filing fees, business licenses, and unemployment insurance vary widely
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-600 mr-2 mt-0.5">•</span>
                <div>
                  <strong>Quality of life:</strong> Climate, culture, and proximity to clients/family matter too
                </div>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-lg border shadow-sm p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">State Tax Structures Explained</h2>
            
            <div className="space-y-4 text-sm">
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">No Income Tax (9 states)</h3>
                <p className="text-slate-700">
                  These states don't tax wage income at all. Great for high earners, but watch out for other taxes.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Flat Tax</h3>
                <p className="text-slate-700">
                  One rate applies to all income levels. Examples: CO (4.4%), IL (4.95%), NC (4.5%). Simple and predictable.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Progressive Tax</h3>
                <p className="text-slate-700">
                  Tax rate increases with income brackets. Most states use this system. Can range from 2-13% depending on income level.
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <p className="text-xs text-slate-700">
                  <strong>Note:</strong> These calculations are simplified estimates. Actual taxes depend on deductions, credits,
                  local taxes, and filing status. Consult a tax professional for personalized advice.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg p-8 text-white text-center">
          <h2 className="text-2xl font-bold mb-3">Calculate Your Total Tax Liability</h2>
          <p className="mb-6 text-primary-50">
            See how federal + state + self-employment taxes impact your take-home income
          </p>
          <Link
            to="/"
            className="inline-block bg-white text-primary-700 px-8 py-3 rounded-lg font-semibold hover:bg-primary-50 transition-colors"
          >
            Use Tax Calculator →
          </Link>
        </div>

        {/* Recent Posts */}
        <div className="mt-12">
          <RecentPosts />
        </div>
      </div>
    </div>
    </>
  );
}
